'use client';
import { useTranslations } from 'next-intl';
import { useState, useCallback } from 'react';
import { Button, ButtonProps } from '@/components/ui/button';
import Icon, { IconName } from '@/components/ui/Icon';
import { useIsMobile } from '@/hooks/useIsMobile';
import { logger } from '@/lib/clientSideLogger';

export function ShareProfileButton({
  size = 'large',
  variant = 'tertiary',
  className,
  username,
  userId,
}: {
  size?: ButtonProps['size'];
  variant?: ButtonProps['variant'];
  className?: string;
  username: string;
  userId: string;
}) {
  const t = useTranslations('ShareProfileButton');
  const [copied, setCopied] = useState(false);
  const [isMobile] = useIsMobile();
  const shareProfile = useCallback(async () => {
    const url = `${window.location.origin}/profile/${userId}`;

    // Copy URL to clipboard only on desktop
    if (!isMobile) {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset "Copied" state after 2 seconds
    }
    // Use Web Share API if available
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${username}'s Profile:`,
          text: `Take a look at ${username}'s profile on Haven`,
          url: url,
        });
      } catch (error) {
        if (error instanceof Error && error.message === 'Share canceled') {
          //do nothing
          return;
        } else {
          if (error instanceof Error) {
            logger('error', error);
          } else {
            logger('error', new Error(String(error)));
          }
        }
      }
    }
  }, [username, isMobile, userId]);

  return (
    <Button
      className={className}
      variant={variant}
      onClick={shareProfile}
      size={size}
    >
      {copied && <Icon variant={IconName.Check} />}
      <span>{copied ? t('copied') : t('title')}</span>
    </Button>
  );
}
